import { css } from '@emotion/react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';

import { ApiOrgMarket } from '@kaiizen/kai-pro-api';
import { useAuth, useKaiProApiClient, useUserMutators } from '@kaiizen/kai-pro-api-react-state';

import { MarketPanel, NoRecordsPlaceholder, RecordListHeading } from '@/Client/Partials';
import { ANALYTICS_EVENTS, trackEvent } from '../../analytics';
import searchIcon from '../../Assets/search-v2.svg';
import { PageLayout } from '../../Layout';
import { usePageTitle, usePersistedSearch, usePersistedSort } from '../../Lib';
import { useAutoSwitchOrgForResource, useOrgMarket, useOrgMarkets, useUsersByOrgMarketId } from '../../State';
import { DataTable } from '../../Widgets';
import { GenericLoadingPage } from '../GenericPages';

const SEARCH_BAR_STYLE = css`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #ffffff;
  border: 1px solid #cfcfcf;
  height: 42px;
  width: 360px;
  padding: 0 10px;
`;

const SEARCH_ICON_STYLE = css`
  width: 18px;
  height: 18px;
  opacity: 0.45;
  filter: brightness(0) saturate(100%);
`;

const SEARCH_COLLAPSED_BUTTON_STYLE = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  background: #ffffff;
  border: 1px solid #cfcfcf;
  cursor: pointer;
  padding: 0;
`;

const SEARCH_ICON_BUTTON_STYLE = css`
  background: transparent;
  border: none;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const SEARCH_INPUT_STYLE = css`
  flex: 1;
  background: transparent;
  border: none;
  color: #1f1f1f;
  outline: none;

  &::placeholder {
    color: #767676;
  }

  &::-webkit-search-cancel-button {
    -webkit-appearance: none;
    appearance: none;
    display: none;
  }
`;

const SEARCH_CLOSE_BUTTON_STYLE = css`
  position: relative;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  width: 18px;
  height: 18px;
  flex-shrink: 0;

  &::before,
  &::after {
    content: '';
    position: absolute;
    left: 8px;
    top: 1px;
    width: 2px;
    height: 16px;
    background: #1f1f1f;
    border-radius: 1px;
  }

  &::before {
    transform: rotate(45deg);
  }

  &::after {
    transform: rotate(-45deg);
  }
`;

const UsersCell = ({ orgMarketId }: { orgMarketId: string }) => {
  const { users } = useUsersByOrgMarketId(orgMarketId);
  if (!users) return <span />;
  const label = users.length === 0
    ? ''
    : users.map(u => u.name || u.phoneNumber || u.email || `User ${u.id}`).join(', ');
  return <span>{label}</span>;
};

export const MarketsPage = () => {
  let { id: marketId } = useParams();
  const navigate = useNavigate();
  usePageTitle('Markets');
  const [searchParams, setSearchParams] = useSearchParams();
  const showArchived = searchParams.get('archived') === 'true';
  const { markets: activeMarkets, marketsMutators } = useOrgMarkets({ archived: false });
  const { markets: archivedMarkets } = useOrgMarkets({ archived: true });
  const { searchText, setSearchText, debouncedSearchText, clearSearch } = usePersistedSearch('markets:search');
  const { order: sortOrder, orderBy: sortOrderBy, onSortChange } = usePersistedSort('markets:sort', 'desc', 'createdDate');
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const { markets: searchedMarkets } = useOrgMarkets({ archived: showArchived, search: debouncedSearchText || undefined });
  const baseMarkets = showArchived ? archivedMarkets : activeMarkets;
  const hasActiveSearch = !!debouncedSearchText.trim();
  const markets = hasActiveSearch ? (searchedMarkets ?? baseMarkets ?? []) : (baseMarkets ?? []);
  const kaiProApiClient = useKaiProApiClient();
  const { refetchAllUsers } = useUserMutators();
  const { userCan } = useAuth();
  const canCreateMarket = userCan('create', 'OrgMarket');
  const canUpdateMarket = userCan('update', 'OrgMarket');
  const { isError, error } = useOrgMarket(marketId);

  useAutoSwitchOrgForResource(
    { error: isError ? error : undefined, orgMarketId: marketId },
  );

  useEffect(() => {
    if (isSearchExpanded) searchInputRef.current?.focus();
  }, [isSearchExpanded]);

  if (!baseMarkets) return <GenericLoadingPage />;

  const marketToEdit = markets?.find(market => market.id === marketId);
  const newMarket = marketId === 'new';
  if (newMarket) marketId = undefined;
  const flyoutOpen = newMarket || !!marketToEdit;

  const addMarket = async (name: string, zipCodes: string[], orgContractorIds: string[], defaultTemplateId?: string | null, defaultManagerIds?: string[]) => {
    const newMarket = await marketsMutators.create(
      { name, zipCodes, defaultTemplateId: defaultTemplateId || undefined, defaultManagerIds: defaultManagerIds ?? [] } as any,
    );
    await Promise.all(orgContractorIds.map(orgContractorId => marketsMutators.addContractor(newMarket.id, orgContractorId)));
    trackEvent(ANALYTICS_EVENTS.marketCreated, {
      marketId: newMarket.id,
      name: newMarket.name,
      zipCodeCount: zipCodes.length,
      contractorCount: orgContractorIds.length,
    });
    return newMarket;
  };

  const closeFlyout = () => {
    navigate('/markets');
  };

  const updateMarket = async (
    marketId: string,
    name: string,
    zipCodes: string[],
    orgContractorIds: string[],
    defaultTemplateId?: string | null,
    defaultManagerIds?: string[],
  ) => {
    // Handle contractor changes first
    const existingContractorIds = marketToEdit?.marketContractors?.map(c => c.orgContractor.id) || [];
    const contractorsToAdd = orgContractorIds.filter(id => !existingContractorIds.includes(id));
    const contractorsToRemove = existingContractorIds.filter((id: string) => !orgContractorIds.includes(id));

    // Process contractor changes first (these will invalidate cache)
    await Promise.all([
      ...contractorsToAdd.map((orgContractorId: string) => marketsMutators.addContractor(marketId, orgContractorId)),
      ...contractorsToRemove.map((orgContractorId: string) => marketsMutators.removeContractor(marketId, orgContractorId)),
    ]);

    // Update market data using mutator to properly update cache
    await marketsMutators.update(marketId, {
      name,
      zipCodes,
      ...(defaultTemplateId != null ? { defaultTemplateId: defaultTemplateId || null } : {}),
      ...(defaultManagerIds != null ? { defaultManagerIds } : {}),
    });

    trackEvent(ANALYTICS_EVENTS.marketUpdated, {
      marketId,
      name,
      zipCodeCount: zipCodes.length,
      contractorCount: orgContractorIds.length,
    });

    closeFlyout();
  };

  const updateUsersForMarket = async (marketId: string, userIds: string[]) => {
    const existingUsers = await kaiProApiClient.getUsersByOrgMarketId(marketId);
    const existingUserIds = existingUsers.map(u => u.id);
    const usersToAdd = userIds.filter(id => !existingUserIds.includes(id));
    const usersToRemove = existingUserIds.filter(id => !userIds.includes(id));
    await Promise.all([
      ...usersToAdd.map(userId => kaiProApiClient.addUserToOrgMarket(marketId, userId)),
      ...usersToRemove.map(userId => kaiProApiClient.removeUserFromOrgMarket(marketId, userId)),
    ]);
    await refetchAllUsers();
  };

  let displayMarkets = markets
    .map((market: ApiOrgMarket) => ({
      id: market.id,
      name: market.name,
      contractors: market.marketContractors?.map(c => c.orgContractor.name).join(', ') || '',
      users: market.id,
      zipCodes: (market as any)?.zipCodes?.join(', ') || '',
      createdDate: market.createdAt,
      updatedDate: market.updatedAt,
      link: `/market/${market.id}`,
      menu: [
        {
          label: canUpdateMarket ? 'Edit Market Details' : 'View Market Details',
          action: () => {
            navigate(`/market/${market.id}`);
          },
        },
        ...(canUpdateMarket
          ? [{
            label: market.archivedAt ? 'Unarchive' : 'Archive',
            action: async () => {
              const isArchiving = !market.archivedAt;
              const payload = {
                archivedAt: market.archivedAt ? null : new Date().toISOString(),
              };
              await marketsMutators.update(market.id, payload);
              // force invalidate both active and archived queries so markets move between lists correctly
              await marketsMutators.refetchAllMarkets();
              trackEvent(isArchiving ? ANALYTICS_EVENTS.marketArchived : ANALYTICS_EVENTS.marketUnarchived, {
                marketId: market.id,
                name: market.name,
              });
            },
          }]
          : []),
      ],
    }));

  const loading = displayMarkets === null;
  const haveMarkets = !loading && displayMarkets.length > 0;
  const hasActiveSearchInput = !!searchText.trim();
  const shouldShowExpandedSearch = isSearchExpanded || !!searchText.trim();

  return (
    <PageLayout
      title={'Markets' + (showArchived ? ' (archived)' : '')}
      contentLayoutStyling={'standard'}
    >
      <RecordListHeading
        recordType='Market'
        activeCount={activeMarkets?.length ?? 0}
        archivedCount={archivedMarkets?.length ?? 0}
        showArchived={showArchived}
        displayCountOverride={hasActiveSearch ? displayMarkets.length : undefined}
        actionNodes={[
          shouldShowExpandedSearch
            ? (
              <div key='marketSearch' css={SEARCH_BAR_STYLE}>
                <button
                  css={SEARCH_ICON_BUTTON_STYLE}
                  type='button'
                  onClick={() => {
                    if (!searchText.trim()) setIsSearchExpanded(false);
                  }}
                  aria-label='Search'
                >
                  <img src={searchIcon} css={SEARCH_ICON_STYLE} alt='Search' />
                </button>
                <input
                  ref={searchInputRef}
                  css={SEARCH_INPUT_STYLE}
                  type='search'
                  value={searchText}
                  onChange={event => setSearchText(event.target.value)}
                  placeholder='Search by market or contractor'
                  aria-label='Search markets'
                />
                {!!searchText.trim() && (
                  <button
                    css={SEARCH_CLOSE_BUTTON_STYLE}
                    type='button'
                    onClick={() => {
                      clearSearch();
                      setIsSearchExpanded(false);
                    }}
                    aria-label='Clear and close market search'
                  />
                )}
              </div>
            )
            : (
              <button
                key='marketSearchCollapsed'
                css={SEARCH_COLLAPSED_BUTTON_STYLE}
                type='button'
                onClick={() => setIsSearchExpanded(true)}
                aria-label='Open market search'
              >
                <img src={searchIcon} css={SEARCH_ICON_STYLE} alt='Search' />
              </button>
            ),
        ]}
        onCreate={canCreateMarket ? () => navigate('/market/new') : undefined}
        onToggleArchived={() => {
          const newParams = new URLSearchParams(searchParams);
          if (showArchived) {
            newParams.delete('archived');
          } else {
            newParams.set('archived', 'true');
          }
          setSearchParams(newParams);
        }}
      />
      {!haveMarkets
        ? (
          <NoRecordsPlaceholder
            recordType='Markets'
            qualifier={showArchived ? 'Archived' : undefined}
            description={hasActiveSearchInput
              ? 'No markets found. Try a different market or contractor name.'
              : canCreateMarket
              ? 'Create your first market to get started.'
              : 'No markets have been created yet.'}
            createButtonText={hasActiveSearchInput ? 'Clear Search' : canCreateMarket ? 'Add a Market' : undefined}
            createFirstRecord={hasActiveSearchInput
              ? (() => {
                clearSearch();
                setIsSearchExpanded(false);
              })
              : canCreateMarket
              ? (() => navigate('/market/new'))
              : undefined}
            iconType='market'
          />
        )
        : (
          <DataTable
            recordType='market'
            items={displayMarkets}
            defaultOrder={sortOrder}
            defaultOrderBy={sortOrderBy}
            onSortChange={onSortChange}
            columns={[
              { name: 'createdDate', sortable: true, sortMethod: 'date', width: '120px', format: (value: string) => new Date(value).toLocaleDateString() },
              { name: 'updatedDate', sortable: true, sortMethod: 'date', width: '120px', format: (value: string) => new Date(value).toLocaleDateString() },
              { name: 'name', sortable: true },
              {
                name: 'zipCodes',
                sortable: false,
                format: (value: string) => {
                  if (!value) return '';
                  return value.length > 100 ? `${value.substring(0, 100)}...` : value;
                },
              },
              { name: 'contractors', sortable: false },
              { name: 'users', sortable: false, format: (orgMarketId: string) => <UsersCell orgMarketId={orgMarketId} /> },
            ]}
          />
        )}
      <MarketPanel
        flyoutOpen={flyoutOpen}
        closeFlyout={closeFlyout}
        marketToEdit={marketToEdit}
        updateMarket={updateMarket}
        addMarket={addMarket}
        updateUsersForMarket={updateUsersForMarket}
        readOnly={!canUpdateMarket && !!marketToEdit}
      />
    </PageLayout>
  );
};
